/// <amd-module name="Wit/BoardsHub/Common/IBoardsWorkItemService" />

import { getTFSProject } from "Tfs/Platform/Page";
import { Services } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import {
    ClientWorkItemService,
    ClientWorkItemServiceEvent,
    IClientWorkItemService,
    IReorderInIterationOptions
} from "Wit/ClientWorkItemService/IClientWorkItemService";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { WorkItem } from "Wit/WorkItemModel/WorkItem/WorkItem";
import { BoardsObservableWorkItem, BoardsWorkItem } from "./BoardsWorkItem";
import { IBoardsWorkItem, IObservableBoardsWorkItem } from "./Interfaces";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";

export interface IBoardsWorkItemService extends IClientWorkItemService<IObservableBoardsWorkItem> {
    /**
     * Clear all loaded work items from hydrated client work items
     */
    clearWorkItemModels(): void;
    /**
     * Add a new work item to the board
     */
    createNewWorkItem(
        extensionId: string,
        workItemType: string,
        fields: INamedFieldDataDictionary,
        previousId?: number,
        nextId?: number,
        workItem?: IWorkItem
    ): Promise<IObservableBoardsWorkItem | undefined>;
    loadWorkItems(project: string, workItemIds: number[], fieldEntries: IFieldEntry[], extensionId: string): Promise<BoardsWorkItem[] | undefined>;
}

class BoardsWorkItemService extends ClientWorkItemService<IObservableBoardsWorkItem> implements IBoardsWorkItemService {
    public clearWorkItemModels(): void {
        const clientWorkItems = this.getAllItems();
        for (const clientWorkItem of clientWorkItems) {
            clientWorkItem.value.clearWorkItemModel();
        }

        const workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        workItemModelService.clearWorkItems();
    }

    public createObservableWorkItem(workItem: IBoardsWorkItem): IObservableBoardsWorkItem {
        return new BoardsObservableWorkItem(workItem);
    }

    public async createNewWorkItem(
        extensionId: string,
        workItemType: string,
        fields: INamedFieldDataDictionary,
        previousId?: number,
        nextId?: number,
        workItem?: IWorkItem
    ): Promise<IObservableBoardsWorkItem | undefined> {
        const projectId = getTFSProject(this.pageContext)?.id ?? "";
        const workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        const workItemTypeService = this.pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");

        const workItemTypeDataPromise = workItemTypeService.getWorkItemTypeData(projectId, [workItemType]);
        let fieldEntryPromise: Promise<IFieldEntry[] | undefined>;
        if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)) {
            const fieldService = this.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
            fieldEntryPromise = fieldService.loadFields();
        } else {
            const fieldService = this.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
            fieldEntryPromise = fieldService.getFieldsAsync();
        }
        
        const [workItemTypeDataList, fieldEntries] = await Promise.all([workItemTypeDataPromise, fieldEntryPromise]);
        const workItemTypeData = workItemTypeDataList.length && workItemTypeDataList[0];
        if (!workItemTypeData) {
            console.error(`Failed to load work item type data for type ${workItemType}`);
            return;
        }

        const tempId = workItem ? workItem.id : WorkItem.getTempId();
        const workItemPromise = workItem ? Promise.resolve(workItem) : workItemModelService.createWorkItem(workItemTypeData, projectId, { tempId });

        const initialFields = {
            ...fields,
            [CoreFieldRefNames.WorkItemType]: workItemType,
            [CoreFieldRefNames.Id]: tempId
        };
        const clientWorkItem = new BoardsWorkItem(initialFields, fieldEntries, extensionId, this.pageContext, workItemPromise);
        for (const [fieldName, fieldValue] of Object.entries(fields)) {
            clientWorkItem.setFieldValue(fieldName, fieldValue);
        }

        const observableItem = this.add(clientWorkItem);
        this._notify({ item: clientWorkItem }, ClientWorkItemServiceEvent.NewWorkItemAdded);

        const teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        const teamSettings = await teamSettingsService.getTeamSettings();
        if (teamSettings) {
            clientWorkItem.setFieldValue(teamSettings.teamFieldName, teamSettings.teamFieldDefaultValue);

            const iteration = teamSettings.defaultIteration ?? teamSettings.currentIteration;
            if (iteration) {
                clientWorkItem.setFieldValue(CoreFieldRefNames.IterationPath, iteration.friendlyPath);
            }
        }

        observableItem.value.saving = true;
        observableItem.notify();

        // if there are rules that make this item invalid, we have to wait until it is successfully saved to reorder it
        this._afterNextSave.set(observableItem.value, () => {
            this._items.delete(tempId);
            this._items.set(clientWorkItem.id, observableItem);

            // reorder the work item to the top or bottom
            if (previousId || nextId) {
                this.reorderWorkItems(observableItem.value.id, projectId, teamSettings?.teamId ?? "", previousId, nextId);
            }

            // fire a second notify so listeners can react to full work item
            this._notify({ item: clientWorkItem }, ClientWorkItemServiceEvent.WorkItemUpdated);
        });

        const showWorkItemDialogWithErrors = () => {
            const service = this.pageContext.getService<IWorkItemService>("work-item-service");

            service.openWorkItemWithProps({
                workItem: observableItem.value.iWorkItem.value,
                onDismiss: () => {
                    if (observableItem.value.id > 0) {
                        // item was saved, nothing to do
                        return;
                    }
                    // item was not saved, remove it
                    this._items.delete(observableItem.value.id);
                    this._notify({ item: observableItem.value }, ClientWorkItemServiceEvent.WorkItemRemoved);
                },
                onSaveComplete: () => {
                    observableItem.saveComplete();
                },
                workItemTypeName: observableItem.value.getRequiredFieldValue<string>(CoreFieldRefNames.WorkItemType)
            });
        }

        if (await observableItem.value.isValid(this.pageContext)) {
            try {
                await observableItem.value.iWorkItem.value!.save();
                observableItem.saveComplete();
                workItemModelService.addWorkItemToMap(observableItem.value.iWorkItem.value!.id, observableItem.value.iWorkItem.value!);
            } catch {
                showWorkItemDialogWithErrors();
            }
        } else {
            showWorkItemDialogWithErrors();
        }

        return observableItem;
    }

    public async loadWorkItems(
        project: string,
        workItemIds: number[],
        fieldEntries: IFieldEntry[],
        extensionId: string
    ): Promise<BoardsWorkItem[] | undefined> {
        const service = this.pageContext.getService<IWorkItemService>("work-item-service");
        try {
            const resolvedWorkItems = (
                await Promise.all(
                    service.loadWorkItems(
                        project,
                        workItemIds,
                        fieldEntries.map(field => field.referenceName)
                    )
                )
            ).flat();
            return resolvedWorkItems.map(workItem => new BoardsWorkItem(workItem.fields, fieldEntries, extensionId));
        } catch (ex) {
            console.log(`Failed to load additional work items with error: ${ex?.message}`);
            return;
        }
    }

    public async reorderWorkItems(
        itemId: number,
        projectId: string,
        teamId: string,
        previousId?: number,
        nextId?: number,
        iterationPath?: string,
        options?: IReorderInIterationOptions,
        orderFieldName?: string
    ): Promise<void> {
        const observableItem = this.get(itemId);
        if (!observableItem) {
            return;
        }

        observableItem.saving();
        await observableItem.value.hydrate(this.pageContext);
        await super.reorderWorkItems(itemId, projectId, teamId, previousId, nextId, iterationPath, options, orderFieldName);
        observableItem.saveComplete();
    }
}

Services.add("IBoardsWorkItemService", { serviceFactory: BoardsWorkItemService });
